import { CompositePropagator, W3CTraceContextPropagator } from '@opentelemetry/core';
import { AWSXRayIdGenerator } from '@opentelemetry/id-generator-aws-xray';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { AWSXRayPropagator } from '@opentelemetry/propagator-aws-xray';
import { AlwaysOffSampler, WebTracerProvider } from '@opentelemetry/sdk-trace-web';

/**
 * Trace context propagation for outgoing backend requests.
 *
 * Generates an X-Ray compatible trace id per request and propagates it in both the
 * X-Ray (`X-Amzn-Trace-Id`) and OpenTelemetry (`traceparent`) headers, so downstream
 * service logs can be correlated with the client-originated call.
 *
 * No span processor / exporter is registered: trace ids are still generated and
 * propagated, but no span data leaves the browser.
 *
 * Every request is propagated as unsampled (`Sampled=0` / trace-flags `00`) so the
 * sampling decision belongs to the backend, which cannot be influenced by a client.
 * The trace id is unaffected by this and is always propagated in full.
 *
 * To start recording client spans later, swap the sampler (e.g. `AlwaysOnSampler` or
 * `TraceIdRatioBasedSampler`) and add a span processor / exporter.
 */

/**
 * The instrumentations only inject trace headers into same-origin requests unless the
 * target is explicitly allowlisted. `VITE_API_URL` is a different origin in every
 * environment, so without this the headers are silently dropped.
 *
 * Scoped to our own API only — propagating to third parties (Cognito, Sentry, Firebase)
 * would add unexpected headers to their CORS preflights and break those requests.
 */
const getApiOriginPattern = (): RegExp[] => {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    return [];
  }

  const { origin } = new URL(apiUrl);
  // eslint-disable-next-line unicorn/prefer-string-replace-all -- replaceAll is ES2021; this project targets ES2020.
  const escapedOrigin = origin.replace(/[$()*+.?[\\]^{|}]/g, String.raw`\$&`);
  return [new RegExp(`^${escapedOrigin}(/|$)`)];
};

try {
  const propagateTraceHeaderCorsUrls = getApiOriginPattern();

  const provider = new WebTracerProvider({
    // The X-Ray propagator reuses the first 4 bytes of the trace id as the X-Ray
    // epoch-seconds field. The default random id generator puts random bytes there,
    // producing timestamps X-Ray rejects, so an X-Ray aware generator is required.
    idGenerator: new AWSXRayIdGenerator(),
    // Propagate the trace id but never claim a sampling decision; the backend decides.
    sampler: new AlwaysOffSampler(),
  });

  provider.register({
    propagator: new CompositePropagator({
      propagators: [
        new AWSXRayPropagator(), // Xray trace propagation
        new W3CTraceContextPropagator(), // Otel trace propagation
      ],
    }),
  });

  registerInstrumentations({
    instrumentations: [
      new XMLHttpRequestInstrumentation({ propagateTraceHeaderCorsUrls }), // For Axios calls
      new FetchInstrumentation({ propagateTraceHeaderCorsUrls }), // For Fetch calls
    ],
  });
} catch (error) {
  // Tracing is best-effort telemetry; never let it stop the app from booting.
  console.warn('Tracing initialization failed; continuing without tracing', error);
}
